import React from "react";
import useEmblaCarousel from "embla-carousel-react";

import { TestWrapper } from "@ui/Atoms";

import { useResize } from "@hooks/useResize";
import { useTelemetry } from "@hooks/useTelemetry";

import type { Props, TabProps } from "./Tabs.types";
import { getEmblaConfig } from "./Tabs.helpers";
import * as Styled from "./Tabs.styled";

export const Tabs: React.FC<Props> = (props): React.ReactElement | null => {
  const { elementClick } = useTelemetry();
  const { windowWidth } = useResize();

  const emblaConfig = getEmblaConfig(props.breakpointsVersion);
  const [emblaRef, emblaApi] = useEmblaCarousel(emblaConfig);

  const $tabsWrapper = React.useRef<HTMLDivElement>(null);
  const children = React.Children.toArray(props.children).filter(child =>
    React.isValidElement<TabProps>(child),
  ) as React.ReactElement<TabProps>[];
  const childrenAmount = children.length;

  const [height, setHeight] = React.useState<number | null>(null);
  const [activeTab, setActiveTab] = React.useState<string | null>(
    children[0]?.props.id || null,
  );

  const clickHandler = (tabProps: TabProps, index: number) => {
    elementClick(tabProps.buttonLabel, {
      buttonId: "" + index,
    });

    setActiveTab(tabProps.id);

    if (emblaApi) {
      emblaApi.scrollTo(index);
    }
  };

  React.useEffect(() => {
    if (props.animation === "opacity") return;
    if (childrenAmount === 1) return;
    if (!$tabsWrapper.current) return;

    const $activeTab = $tabsWrapper.current.querySelector(
      "[data-active='true']",
    ) as HTMLDivElement;

    setHeight($activeTab?.clientHeight || null);
  }, [$tabsWrapper, activeTab, windowWidth, childrenAmount, props.animation]);

  if (!childrenAmount) {
    return null;
  }

  return (
    <TestWrapper testId={props.testId}>
      <Styled.Root $isDarkMode={props.isDarkMode}>
        <Styled.Embla ref={emblaRef}>
          <Styled.EmblaContent>
            {children.map((child, index) => (
              <Styled.EmblaSlide key={child.props.id}>
                <TestWrapper testId={child.props.testId}>
                  <Styled.NavButton
                    type="button"
                    data-active={child.props.id === activeTab}
                    onClick={clickHandler.bind(null, child.props, index)}
                  >
                    {child.props.buttonLabel}
                  </Styled.NavButton>
                </TestWrapper>
              </Styled.EmblaSlide>
            ))}
          </Styled.EmblaContent>
        </Styled.Embla>

        <Styled.TabsWrapper
          ref={$tabsWrapper}
          $height={height}
          $animation={props.animation || "full"}
        >
          {children.map((child, index) => {
            if (!React.isValidElement(child)) {
              return null;
            }

            return (
              <TestWrapper key={index} testId={child.props.testId}>
                <Styled.Tab data-active={child.props.id === activeTab}>
                  {child.props.children}
                </Styled.Tab>
              </TestWrapper>
            );
          })}
        </Styled.TabsWrapper>
      </Styled.Root>
    </TestWrapper>
  );
};
